import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";
import ApiRequest from "../../helper/ApiRequest";
import { ApiEndpoints } from "../../helper/ApiEndpoints";

const emptyFaq = () => ({ question: "", answer: "" });
const normalizeFaqs = (faqs) => {
  if (!Array.isArray(faqs)) return [emptyFaq()];

  const normalized = faqs
    .map((faq) => ({
      question: faq?.question || "",
      answer: faq?.answer || "",
    }))
    .filter((faq) => faq.question || faq.answer);

  return normalized.length ? normalized : [emptyFaq()];
};

export default function FaqForm({ mode }) {
  const isEdit = mode === "edit";
  const { faq_id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [faqData, setFaqData] = useState({
    url: "",
    status: "0",
    faqs: [emptyFaq()],
  });

  useEffect(() => {
    if (!isEdit) return;

    const fetchFaq = async () => {
      try {
        const response = await ApiRequest("GET", `${ApiEndpoints.faq.faqEdit}${faq_id}`);
        if (response.error) {
          toast.error(response.message);
          navigate("/faq");
          return;
        }

        setFaqData({
          url: response.faq?.url || "",
          status: String(response.faq?.status ?? "0"),
          faqs: normalizeFaqs(response.faq?.faqs),
        });
      } catch (error) {
        toast.error(error.response?.data?.message || "Unable to load the FAQ.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchFaq();
  }, [faq_id, isEdit, navigate]);

  const handleFaqChange = (index, field, value) => {
    setFaqData((current) => ({
      ...current,
      faqs: current.faqs.map((faq, faqIndex) =>
        faqIndex === index ? { ...faq, [field]: value } : faq
      ),
    }));
  };

  const addFaq = () => {
    setFaqData((current) => ({ ...current, faqs: [...current.faqs, emptyFaq()] }));
  };

  const removeFaq = (index) => {
    if (faqData.faqs.length === 1) {
      toast.error("At least one question and answer is required.");
      return;
    }

    setFaqData((current) => ({
      ...current,
      faqs: current.faqs.filter((_, faqIndex) => faqIndex !== index),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = {
      ...faqData,
      url: faqData.url.trim(),
      faqs: faqData.faqs.map((faq) => ({
        question: faq.question.trim(),
        answer: faq.answer.trim(),
      })),
    };

    if (!payload.url) {
      toast.error("URL is mandatory.");
      return;
    }

    if (payload.faqs.some((faq) => !faq.question || !faq.answer)) {
      toast.error("Question and answer fields are mandatory.");
      return;
    }

    if (isEdit) payload.faq_id = faq_id;

    setIsSaving(true);
    try {
      const endpoint = isEdit ? ApiEndpoints.faq.faqUpdate : ApiEndpoints.faq.faqCreate;
      const response = await ApiRequest("POST", endpoint, payload);
      if (response.error) {
        toast.error(response.message);
        return;
      }

      toast.success(response.message);
      navigate("/faq");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to save the FAQ.");
    } finally {
      setIsSaving(false);
    }
  };

  const pagename = {
    name: "FAQ Management",
    user: "Admin",
    innerpage: `FAQ Management / ${isEdit ? "Edit" : "Add"} FAQ`,
  };

  return (
    <>
      <AdminHeader pagename={pagename} />
      <div className="add-content-page">
        {isLoading ? (
          <div className="add-content-wrap">Loading...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="add-content-wrap">
              <div className="row">
                <div className="col-lg-8">
                  <div className="add-page-inputs">
                    <div className="label-wrap-add"><label>URL<small>*</small></label></div>
                    <input
                      type="text"
                      name="url"
                      value={faqData.url}
                      onChange={(event) => setFaqData((current) => ({ ...current, url: event.target.value }))}
                      className="form-control"
                      placeholder="https://www.example.com/page"
                    />
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="add-page-inputs">
                    <div className="label-wrap-add"><label>Status</label></div>
                    <select
                      name="status"
                      value={faqData.status}
                      onChange={(event) => setFaqData((current) => ({ ...current, status: event.target.value }))}
                      className="form-control"
                    >
                      <option value="0">Enable</option>
                      <option value="1">Disable</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {faqData.faqs.map((faq, index) => (
              <div className="add-content-wrap" key={index}>
                <div className="row">
                  <div className="col-lg-12">
                    <div className="add-page-sections">Question {index + 1}</div>
                  </div>
                  <div className="col-lg-12">
                    <div className="add-page-inputs">
                      <div className="label-wrap-add"><label>Question<small>*</small></label></div>
                      <input
                        type="text"
                        value={faq.question}
                        onChange={(event) => handleFaqChange(index, "question", event.target.value)}
                        className="form-control"
                      />
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="add-page-inputs">
                      <div className="label-wrap-add"><label>Answer<small>*</small></label></div>
                      <textarea
                        value={faq.answer}
                        onChange={(event) => handleFaqChange(index, "answer", event.target.value)}
                        className="form-control"
                        rows="4"
                        style={{ height: "auto" }}
                      />
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <button type="button" className="default-btn" onClick={() => removeFaq(index)}>Remove<span></span></button>
                  </div>
                </div>
              </div>
            ))}

            <button type="button" className="addmore-images-client w-100" onClick={addFaq}>Add More<span></span></button>

            <button type="submit" disabled={isSaving} className="default-btn mt-4">
              {isSaving ? "Saving..." : isEdit ? "Update" : "Submit"}<span></span>
            </button>
          </form>
        )}
      </div>
    </>
  );
}
